// ============================================================================
// PROJECT CHANGES CHANNEL — cross-tab refresh signal for the App.jsx
// `projects` state.
// ----------------------------------------------------------------------------
// Pattern derived from the sync-folder cross-tab invalidator at
// `src/services/sync-folder.js:259-294` (folder-sync audit fix 2026-05-11).
//
// **Problem this solves (A3, audit batch v5.32.x):** App.jsx holds a single
// `projects` state populated once on mount via `getAllProjectSummaries()`.
// When tab A saves / deletes / duplicates / marks-status / sends-quote on a
// project, tab B's `projects` array is stale until tab B is reloaded. The
// Dashboard, AllQuotes, and BottomNavBar all read from this array — they
// silently drift apart across tabs.
//
// **Solution:** open a tenant-scoped
// `BroadcastChannel('qm-project-changes::<verifiedTenantId>')` once per tab.
// Every persist-shaped mutation in App.jsx (create, save, delete, duplicate,
// updateQuoteStatus, onPrintOpened, onEmailSent, updateProjectInPlace
// callers) posts a message. Every OTHER tab's listener re-fetches via
// `getAllProjectSummaries()` and replaces its local `projects` state.
//
// **No loop-back:** each tab generates a `tabId` at module load. Messages
// carry their origin tabId; the listener ignores messages whose tabId
// matches its own (which means it would already have updated its local state
// synchronously through the React setState path).
//
// **Fallback for browsers without BroadcastChannel** (older Safari, some
// embedded WebViews): we write a timestamp to `localStorage` under a known
// tenant-scoped key, which triggers a `storage` event in every other tab on
// the same origin. Tabs subscribe to both channels and de-dupe by timestamp.
// ============================================================================

import {
    getVerifiedTenantBrowserKey,
    requireVerifiedTenantRuntimeId,
} from './tenant-runtime-scope';

const CHANNEL_NAME_BASE = 'qm-project-changes';
const STORAGE_KEY_BASE = 'qm:project-changes:lastBroadcast';

function channelNameForTenant(tenantId: string) {
    return `${CHANNEL_NAME_BASE}::${tenantId}`;
}

function storageKeyForTenant(tenantId: string) {
    const key = getVerifiedTenantBrowserKey(STORAGE_KEY_BASE);
    return key && key.endsWith(`::${tenantId}`) ? key : null;
}

function generateTabId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch { /* fall through */ }
    return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// Generated ONCE per page load. Stable across re-renders, unique across tabs.
const _tabId = generateTabId();

let _channel: BroadcastChannel | null = null;
let _channelTenantId: string | null = null;
function _getChannel(tenantId: string): BroadcastChannel | null {
    if (typeof BroadcastChannel === 'undefined') return null;
    if (_channel && _channelTenantId === tenantId) return _channel;
    if (_channel) {
        try { _channel.close(); } catch { /* non-fatal */ }
        _channel = null;
        _channelTenantId = null;
    }
    try {
        _channel = new BroadcastChannel(channelNameForTenant(tenantId));
        _channelTenantId = tenantId;
    } catch {
        _channel = null;
        _channelTenantId = null;
    }
    return _channel;
}

/**
 * Returns the unique id for THIS tab. Exposed for tests + the storage-event
 * fallback (so the timestamp-tag matches the broadcast tag).
 */
export function getTabId() {
    return _tabId;
}

/**
 * Broadcast that one or more projects in the local DB have changed (saved,
 * deleted, duplicated, status-updated, etc.). Other tabs subscribed to this
 * channel will re-fetch the project list via `getAllProjectSummaries()`.
 *
 * @param {object} [payload] - optional metadata
 * @param {string[]} [payload.projectIds] - which project IDs changed
 * @param {string} [payload.kind] - 'save' | 'delete' | 'duplicate' | 'status' | 'print' | 'email' | 'misc'
 */
export function broadcastProjectsChanged(payload: { kind?: string; projectIds?: string[] } = {}) {
    let tenantId: string;
    try {
        tenantId = requireVerifiedTenantRuntimeId();
    } catch {
        return false;
    }
    const message = {
        tenantId,
        tabId: _tabId,
        kind: payload.kind || 'misc',
        projectIds: Array.isArray(payload.projectIds) ? payload.projectIds : [],
        at: Date.now(),
    };
    const ch = _getChannel(tenantId);
    if (ch) {
        try { ch.postMessage(message); } catch { /* channel closed; non-fatal */ }
    }
    // Fallback / belt-and-braces: write to localStorage so tabs without
    // BroadcastChannel (or where the channel post raced a tab close) still
    // get a `storage` event in every other tab.
    const storageKey = storageKeyForTenant(tenantId);
    if (storageKey && typeof localStorage !== 'undefined') {
        try { localStorage.setItem(storageKey, JSON.stringify(message)); } catch { /* quota / privacy mode */ }
    }
    return true;
}

type ProjectChangeMessage = {
    tenantId?: string;
    tabId?: string;
    kind?: string;
    projectIds?: string[];
    at?: number;
};

type ProjectSummaryRefreshCoordinatorOptions<T> = {
    refresh: () => Promise<T> | T;
    onSuccess?: (value: T) => void;
    onError?: (error: unknown) => void;
    debounceMs?: number;
    maxWaitMs?: number;
    baseBackoffMs?: number;
    maxBackoffMs?: number;
};

/**
 * Bound cross-tab change bursts to one project-summary request at a time.
 *
 * A bulk delete deliberately emits many durable project mutations. Before this
 * coordinator, every BroadcastChannel message called GET /projects?summary=true
 * immediately. A 485-project cleanup therefore created 485 overlapping reads
 * in EACH other open tab; once the backend started returning 500, later signals
 * kept hammering it with no single-flight guard or cooldown.
 *
 * The coordinator has four explicit guarantees:
 *  - a short burst is trailing-edge coalesced;
 *  - a continuous burst still refreshes by maxWaitMs;
 *  - only one refresh can be in flight, with at most one trailing refresh;
 *  - failures back off exponentially, and never retry without a new signal.
 *
 * It is intentionally independent of React so the concurrency contract can be
 * exercised deterministically with fake timers.
 */
export function createProjectSummaryRefreshCoordinator<T>({
    refresh,
    onSuccess = () => {},
    onError = () => {},
    debounceMs = 350,
    maxWaitMs = 5000,
    baseBackoffMs = 2000,
    maxBackoffMs = 60000,
}: ProjectSummaryRefreshCoordinatorOptions<T>) {
    if (typeof refresh !== 'function') {
        throw new TypeError('createProjectSummaryRefreshCoordinator requires refresh()');
    }

    const debounce = Math.max(0, Number(debounceMs) || 0);
    const maxWait = Math.max(debounce, Number(maxWaitMs) || debounce);
    const baseBackoff = Math.max(1, Number(baseBackoffMs) || 1);
    const maxBackoff = Math.max(baseBackoff, Number(maxBackoffMs) || baseBackoff);

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let pending = false;
    let burstStartedAt: number | null = null;
    let consecutiveFailures = 0;
    let failureNotBefore = 0;

    const clearScheduled = () => {
        if (timer !== null) clearTimeout(timer);
        timer = null;
    };

    const schedule = (delay: number) => {
        if (stopped || inFlight || !pending) return;
        clearScheduled();
        timer = setTimeout(run, Math.max(0, delay));
    };

    const schedulePending = () => {
        if (stopped || inFlight || !pending) return;
        const now = Date.now();
        if (consecutiveFailures > 0 && now < failureNotBefore) {
            schedule(failureNotBefore - now);
            return;
        }
        if (burstStartedAt === null) burstStartedAt = now;
        const maxWaitRemaining = Math.max(0, maxWait - (now - burstStartedAt));
        schedule(Math.min(debounce, maxWaitRemaining));
    };

    async function run() {
        timer = null;
        if (stopped || inFlight || !pending) return;

        const now = Date.now();
        if (consecutiveFailures > 0 && now < failureNotBefore) {
            schedule(failureNotBefore - now);
            return;
        }

        pending = false;
        burstStartedAt = null;
        inFlight = true;
        try {
            const value = await refresh();
            consecutiveFailures = 0;
            failureNotBefore = 0;
            if (!stopped) onSuccess(value);
        } catch (error) {
            consecutiveFailures += 1;
            const backoff = Math.min(
                maxBackoff,
                baseBackoff * (2 ** Math.max(0, consecutiveFailures - 1)),
            );
            failureNotBefore = Date.now() + backoff;
            if (!stopped) onError(error);
        } finally {
            inFlight = false;
            // No unconditional retry: a failed endpoint remains quiet until a
            // real project-change signal sets pending=true.
            if (pending && !stopped) schedulePending();
        }
    }

    return {
        request(_message: ProjectChangeMessage = {}) {
            if (stopped) return;
            pending = true;
            if (burstStartedAt === null) burstStartedAt = Date.now();
            if (!inFlight) schedulePending();
        },
        stop() {
            stopped = true;
            pending = false;
            burstStartedAt = null;
            clearScheduled();
        },
    };
}

/**
 * Subscribe to project-changed messages from OTHER tabs. The callback
 * receives `{ tabId, kind, projectIds, at }`.
 *
 * Messages originating from THIS tab (matching `getTabId()`) are filtered
 * out — they would already have updated the local React state via the
 * synchronous setState path that called `broadcastProjectsChanged`.
 *
 * @param {(message: { tabId: string, kind: string, projectIds: string[], at: number }) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function subscribeProjectsChanged(callback: (message: { tabId: string; kind: string; projectIds: string[]; at: number }) => void) {
    if (typeof callback !== 'function') return () => {};
    let tenantId: string;
    try {
        tenantId = requireVerifiedTenantRuntimeId();
    } catch {
        return () => {};
    }
    const storageKey = storageKeyForTenant(tenantId);
    if (!storageKey) return () => {};
    let lastSeenAt = 0;

    const handleMessage = (data: any) => {
        if (!data || typeof data !== 'object') return;
        if (getVerifiedTenantBrowserKey(STORAGE_KEY_BASE) !== storageKey) return;
        if (data.tenantId !== tenantId) return;
        if (data.tabId === _tabId) return;             // self — already in sync
        if (!data.at || data.at <= lastSeenAt) return; // already saw this one (storage + channel dedupe)
        lastSeenAt = data.at;
        try { callback(data); } catch (e: any) {
            console.warn('[project-changes] subscriber threw:', e?.message || e);
        }
    };

    const onChannelMessage = (ev: MessageEvent) => handleMessage(ev?.data);

    const onStorage = (ev: StorageEvent) => {
        if (!ev || ev.key !== storageKey) return;
        const raw = ev.newValue;
        if (!raw) return;
        try { handleMessage(JSON.parse(raw)); } catch { /* malformed; ignore */ }
    };

    let channel: BroadcastChannel | null = null;
    try {
        channel = _getChannel(tenantId);
        if (channel) channel.addEventListener('message', onChannelMessage);
    } catch { channel = null; }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        try { window.addEventListener('storage', onStorage); } catch { /* non-fatal */ }
    }

    return function unsubscribe() {
        if (channel) {
            try { channel.removeEventListener('message', onChannelMessage); } catch { /* non-fatal */ }
        }
        if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
            try { window.removeEventListener('storage', onStorage); } catch { /* non-fatal */ }
        }
    };
}

// Exported for tests only.
export const __TEST_ONLY__ = {
    CHANNEL_NAME_BASE,
    STORAGE_KEY_BASE,
    channelNameForTenant,
    storageKeyForTenant,
    resetForTest() {
        if (_channel) {
            try { _channel.close(); } catch { /* non-fatal */ }
        }
        _channel = null;
        _channelTenantId = null;
    },
};
